import {
	BN,
	OracleSource,
	PerpMarketConfig,
	PerpMarkets,
	PublicKey,
	SpotMarketConfig,
	SpotMarkets,
} from '@drift-labs/sdk';
import {
	ENUM_UTILS,
	SerializedSpotMarketConfig,
	SerializedPerpMarketConfig,
	SerializedRemoteConfigs,
} from '@drift/common';
import { PredictionMarketConfig } from './predictionMarkets';
import { sdkConfig } from 'src/environmentVariables/EnvironmentVariables';

export type RemoteConfigs = {
	predictionMarketsConfig: PredictionMarketConfig[];
	spotMarketsConfig: SpotMarketConfig[];
	perpMarketsConfig: PerpMarketConfig[];
};

export class ConfigsManager {
	static DEFAULT_CONFIGS: SerializedRemoteConfigs = {
		marketConfigs: {
			devnet: {
				spot: this.serializeSpotMarketsConfig(SpotMarkets.devnet),
				perp: this.serializedPerpMarketsConfig(PerpMarkets.devnet),
			},
			mainnet: {
				spot: this.serializeSpotMarketsConfig(SpotMarkets['mainnet-beta']),
				perp: this.serializedPerpMarketsConfig(PerpMarkets['mainnet-beta']),
			},
		},
		predictionMarkets: PerpMarkets['mainnet-beta']
			.filter((m) => m.symbol.includes('BET'))
			.map((p, index) =>
				new PredictionMarketConfig({
					title: p.fullName ?? '',
					shortTitle: p.symbol,
					imgSrc: '',
					resolutionDateUnix: p.launchTs,
					category: p.category?.[0] ?? '',
					symbol: p.symbol,
					production: true,
					devnetMarketIndex: PerpMarkets['devnet'][index]?.marketIndex,
					mainnetMarketIndex: p.marketIndex,
				}).serialize()
			),
	};

	static deserializeSpotMarketsConfig(
		config: SerializedSpotMarketConfig[]
	): SpotMarketConfig[] {
		return config
			.map((c) =>
				c
					? {
							...c,
							precision: new BN(c.precision),
							precisionExp: new BN(c.precisionExp),
							oracle: new PublicKey(c.oracle),
							mint: new PublicKey(c.mint),
							oracleSource: ENUM_UTILS.toObj(c.oracleSource) as OracleSource,
							serumMarket: c.serumMarket
								? new PublicKey(c.serumMarket)
								: undefined,
							phoenixMarket: c.phoenixMarket
								? new PublicKey(c.phoenixMarket)
								: undefined,
							openbookMarket: c.openbookMarket
								? new PublicKey(c.openbookMarket)
								: undefined,
						}
					: null
			)
			.filter(Boolean) as SpotMarketConfig[];
	}

	static deserializePerpMarketsConfig(
		config: SerializedPerpMarketConfig[]
	): PerpMarketConfig[] {
		return config.map((c) => ({
			...c,
			category: c.category,
			oracle: new PublicKey(c.oracle),
			oracleSource: ENUM_UTILS.toObj(c.oracleSource) as OracleSource,
		}));
	}

	static deserializeRemoteConfigs(
		config: SerializedRemoteConfigs
	): RemoteConfigs {
		const isMainnet = sdkConfig.ENV === 'mainnet-beta';

		return {
			predictionMarketsConfig:
				config.predictionMarkets?.map((c) => new PredictionMarketConfig(c)) ??
				[],
			spotMarketsConfig: this.deserializeSpotMarketsConfig(
				config.marketConfigs[isMainnet ? 'mainnet' : 'devnet']?.spot ?? []
			),
			perpMarketsConfig: this.deserializePerpMarketsConfig(
				config.marketConfigs[isMainnet ? 'mainnet' : 'devnet']?.perp ?? []
			),
		};
	}

	static serializeSpotMarketsConfig(
		config: SpotMarketConfig[]
	): SerializedSpotMarketConfig[] {
		return config
			.map((c) =>
				c
					? {
							...c,
							precision: c.precision.toNumber(),
							precisionExp: c.precisionExp.toNumber(),
							oracle: c.oracle.toString(),
							mint: c.mint.toString(),
							oracleSource: ENUM_UTILS.toStr(c.oracleSource),
							serumMarket: c.serumMarket?.toString(),
							phoenixMarket: c.phoenixMarket?.toString(),
							openbookMarket: c.openbookMarket?.toString(),
						}
					: null
			)
			.filter(Boolean) as SerializedSpotMarketConfig[];
	}

	static serializedPerpMarketsConfig(
		config: PerpMarketConfig[]
	): SerializedPerpMarketConfig[] {
		return config.map((c) => ({
			...c,
			oracle: c.oracle.toString(),
			oracleSource: ENUM_UTILS.toStr(c.oracleSource),
		}));
	}

	static filterProductionConfigs(
		config: SerializedRemoteConfigs,
		isProduction: boolean
	): SerializedRemoteConfigs {
		if (!isProduction) {
			return config;
		}

		const filteredConfig = {
			marketConfigs: {
				devnet: {
					spot:
						config.marketConfigs?.devnet?.spot?.filter((c) => c.production) ??
						[],
					perp:
						config.marketConfigs?.devnet?.perp?.filter((c) => c.production) ??
						[],
				},
				mainnet: {
					spot:
						config.marketConfigs?.mainnet?.spot?.filter((c) => c.production) ??
						[],
					perp:
						config.marketConfigs?.mainnet?.perp?.filter((c) => c.production) ??
						[],
				},
			},
			predictionMarkets:
				config.predictionMarkets?.filter((config) => config.production) ?? [],
		};

		return filteredConfig;
	}
}
